import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Truck, CheckCircle2 } from 'lucide-react';
import { api } from '../../services/api';
import { useUserPrefs } from '../context/UserPrefs';
import { PENDING_OC_STATUSES } from './purchaseOrderStatus';

// Conecta Almacén con las órdenes de compra de Contabilidad: muestra lo que
// está pedido y aún no ha llegado a la sede, para que el almacenista sepa
// en todo momento qué está pendiente. "Verificar llegada" reutiliza el
// mismo endpoint que ya usa Contabilidad para recepcionar una OC (deja el
// mismo historial, marca la requisición como recibida si corresponde).
// También se puede recepcionar directamente desde "Entrada" seleccionando
// la OC ahí — "Verificar llegada" sigue disponible como respaldo manual
// (OCs de servicios sin entrada física, correcciones, etc.).
function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const PENDING_STATUSES = PENDING_OC_STATUSES;

export default function PurchaseOrdersDeliveryModule({ location, warehouses = [] }) {
  const { currentUser } = useUserPrefs();
  const canVerify = !!(currentUser?.purchaseCanEditOrders) || ['Administrador', 'Contabilidad'].includes(String(currentUser?.role || '').trim());

  const warehouseOptions = useMemo(
    () => (Array.isArray(warehouses) ? warehouses : []).filter((w) => w && !w.parent_id),
    [warehouses]
  );

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [verifyingId, setVerifyingId] = useState(null);
  const [form, setForm] = useState({ date: '', receivedBy: '', reference: '', notes: '', warehouseId: '', photo: null, items: [] });
  const [saving, setSaving] = useState(false);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.accounting.purchaseOrders.list({ location, limit: 500, order: 'desc' });
      const rows = Array.isArray(res?.data) ? res.data : [];
      setOrders(rows.map((o) => ({
        id: o.id,
        code: o.code || '',
        requestCode: o.request_code || o.requestCode || '',
        provider: o.provider || '',
        status: o.status || 'Emitida',
        deliveryDate: o.delivery_date || o.deliveryDate || '',
        createdAt: o.created_at || o.createdAt || '',
        items: Array.isArray(o.items) ? o.items : [],
      })));
    } catch (_) {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [location]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const pending = useMemo(
    () => orders.filter((o) => PENDING_STATUSES.includes(String(o.status))),
    [orders]
  );

  const openVerify = (order) => {
    const defaultWh = String(currentUser?.warehouseId || warehouseOptions[0]?.id || '').trim();
    setVerifyingId(order.id);
    setForm({
      date: new Date().toISOString().slice(0, 10),
      receivedBy: '',
      reference: '',
      notes: '',
      warehouseId: defaultWh,
      photo: null,
      items: (order.items || []).map((it) => ({
        description: it.description || it.name || '',
        unit: it.unit || '',
        ordered: Number(it.quantity || 0) || 0,
        received: Number(it.quantity || 0) || 0,
      })),
    });
  };

  const verifyingHasMaterialCodes = useMemo(() => {
    if (!verifyingId) return false;
    const order = orders.find((o) => o.id === verifyingId);
    return (order?.items || []).some((it) => String(it.material_code || it.materialCode || '').trim() !== '');
  }, [verifyingId, orders]);

  const submitVerify = async () => {
    if (!verifyingId) return;
    if (!form.photo) { window.alert('Adjunta al menos 1 foto de recepción.'); return; }
    if (verifyingHasMaterialCodes && !String(form.warehouseId || '').trim()) {
      window.alert('Selecciona la bodega donde queda la mercancía (se creará la entrada de inventario con costo de la OC).');
      return;
    }
    setSaving(true);
    try {
      const photoDataUrl = await fileToDataUrl(form.photo);
      const res = await api.accounting.purchaseOrders.receive({
        id: verifyingId,
        reception: {
          date: form.date,
          receivedBy: form.receivedBy,
          reference: form.reference,
          notes: form.notes,
          warehouse_id: String(form.warehouseId || '').trim() || undefined,
          photos: [photoDataUrl],
          items: form.items,
        },
      });
      setVerifyingId(null);
      await loadOrders();
      const autoEntradas = Array.isArray(res?.auto_entradas) ? res.auto_entradas : [];
      if (autoEntradas.length > 0) {
        window.alert(`Llegada verificada. Entrada automática en Almacén: ${autoEntradas.length} material(es) con costo de la OC. No vuelvas a registrar el mismo stock en Entrada.`);
      } else {
        window.alert('Llegada verificada. Si algún ítem no entró automáticamente (sin código, serializado, etc.), regístralo desde Entrada.');
      }
    } catch (e) {
      window.alert(e?.message || 'No fue posible registrar la verificación.');
    } finally {
      setSaving(false);
    }
  };

  const verifying = orders.find((o) => o.id === verifyingId) || null;

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-base font-semibold flex items-center gap-2"><Truck className="h-5 w-5" /> Pedidos de compra pendientes por llegar</h4>
        <p className="text-sm text-gray-600">Requisiciones ya con orden de compra emitida, para que sepas qué está pedido antes de que llegue a la sede.</p>
      </div>

      {loading ? (
        <div className="bg-white border rounded-xl px-4 py-10 text-center text-sm text-gray-600">Cargando...</div>
      ) : pending.length === 0 ? (
        <div className="bg-white border rounded-xl px-4 py-10 text-center text-sm text-gray-600">No hay pedidos pendientes por llegar.</div>
      ) : (
        <div className="bg-white border rounded-xl overflow-hidden divide-y">
          {pending.map((o) => (
            <div key={o.id} className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-sm font-medium text-gray-800">{o.code} {o.requestCode ? `· Requisición ${o.requestCode}` : ''}</div>
                <div className="text-xs text-gray-500">
                  {o.provider ? `Proveedor: ${o.provider} · ` : ''}
                  {(o.items || []).length} ítem(s)
                  {o.deliveryDate ? ` · Entrega estimada: ${o.deliveryDate}` : ''}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-1 rounded-full border bg-yellow-50 text-yellow-800 border-yellow-200">{o.status}</span>
                {canVerify && (
                  <button
                    type="button"
                    onClick={() => openVerify(o)}
                    className="px-3 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 text-sm inline-flex items-center gap-1.5"
                  >
                    <CheckCircle2 className="h-4 w-4" /> Verificar llegada
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {verifying && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-lg w-full max-h-[90vh] overflow-y-auto p-5 space-y-3">
            <div className="text-base font-semibold">Verificar llegada · {verifying.code}</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Fecha</label>
                <input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="w-full border rounded px-3 py-2 bg-gray-50" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Recibido por</label>
                <input type="text" value={form.receivedBy} onChange={(e) => setForm((f) => ({ ...f, receivedBy: e.target.value }))} className="w-full border rounded px-3 py-2 bg-gray-50" placeholder="Nombre" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Remisión / factura</label>
              <input type="text" value={form.reference} onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} className="w-full border rounded px-3 py-2 bg-gray-50" />
            </div>
            {verifyingHasMaterialCodes && (
              <div>
                <label className="block text-xs text-gray-500 mb-1">Bodega de recepción *</label>
                <select
                  value={form.warehouseId}
                  onChange={(e) => setForm((f) => ({ ...f, warehouseId: e.target.value }))}
                  className="w-full border rounded px-3 py-2 bg-gray-50"
                >
                  <option value="">Seleccione...</option>
                  {warehouseOptions.map((w) => (
                    <option key={w.id} value={w.id}>{w.name || w.id}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Con código de material, el sistema ingresa stock y costo promedio desde el precio de la OC (igual que en Contabilidad).</p>
              </div>
            )}
            <div>
              <label className="block text-xs text-gray-500 mb-1">Foto de recepción *</label>
              <input type="file" accept="image/*" onChange={(e) => setForm((f) => ({ ...f, photo: (e.target.files && e.target.files[0]) || null }))} className="w-full text-sm" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Ítems recibidos</label>
              <div className="border rounded divide-y">
                {(form.items || []).map((it, idx) => (
                  <div key={idx} className="px-3 py-2 flex items-center justify-between gap-2 text-sm">
                    <div className="flex-1 min-w-0 truncate">{it.description || `Ítem ${idx + 1}`}</div>
                    <div className="text-xs text-gray-500">Pedido: {it.ordered}</div>
                    <input
                      type="number"
                      min="0"
                      value={it.received}
                      onChange={(e) => setForm((f) => ({
                        ...f,
                        items: f.items.map((x, i) => (i === idx ? { ...x, received: Number(e.target.value) || 0 } : x)),
                      }))}
                      className="w-20 border rounded px-2 py-1"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Observaciones</label>
              <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="w-full border rounded px-3 py-2 bg-gray-50 min-h-[60px]" />
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button type="button" onClick={() => setVerifyingId(null)} className="px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300">Cancelar</button>
              <button type="button" onClick={submitVerify} disabled={saving} className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-60">
                {saving ? 'Guardando...' : 'Confirmar verificación'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
